import type { Moment } from 'moment'
import moment from 'moment'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import SearchDatePicker from './SearchDatePicker'
import SearchOccupancyPicker from './SearchOccupancyPicker'
import SearchPlace from './SearchPlace'
import useDefaultDates from '../Common/Hooks/useDefaultDates'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import {
  GlobalSearchDispatchContext,
  GlobalSearchStateContext,
} from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useTripMetadataPrefetch, useTrip } from 'tripPlanner/hooks/api/trip'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { DatesInputTrigger } from './Common/DatesInputTrigger'
import { GuestsInputTrigger } from './Common/GuestsInputTrigger'
import { PlaceInputTrigger } from './Common/PlaceInputTrigger'

interface Props {
  tripId: string
  defaultDate?: string
  sectionId?: string
}

const MIN_SEARCH_DATE = moment().subtract(1, 'day')

const defaultOccupancies: Array<App.Occupants> = [{ adults: 2 }]

function SearchAccommodation({ tripId, sectionId, defaultDate }: Props) {
  const intl = useIntl()
  useTripMetadataPrefetch({ tripId })
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const isMobile = useScreenSizeOnly('mobile')

  const {
    occupancies = defaultOccupancies,
    searchItem,
    checkinDate,
    checkoutDate,
    isFlexibleDateSelected,
  } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const { data: trip } = useTrip({
    tripId,
  })

  const defaultDates = useDefaultDates({
    activeDate: defaultDate,
    tripStartDate: trip?.startDate,
    tripEndDate: trip?.endDate,
  })

  useEffect(() => {
    if (
      checkinDate !== undefined ||
      checkoutDate !== undefined ||
      !defaultDates.start ||
      !defaultDates.end
    ) {
      return
    }

    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_CHECKIN_DATE,
      date: defaultDates.start,
    })
    const defaultEndDate = defaultDates.end.isSame(defaultDates.start) ?
        defaultDates.end.clone().add(1, 'day') :
      defaultDates.end
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_CHECKOUT_DATE,
      date: defaultEndDate,
    })
  }, [checkinDate, checkoutDate, globalSearchDispatch, defaultDates])

  const onSetDates = useCallback(
    (startDate?: Moment, endDate?: Moment) => {
      if (startDate) {
        globalSearchDispatch({
          type: GlobalSearchStateActions.SET_CHECKIN_DATE,
          date: startDate,
        })
      }

      if (endDate) {
        globalSearchDispatch({
          type: GlobalSearchStateActions.SET_CHECKOUT_DATE,
          date: endDate,
        })
      }
    },
    [globalSearchDispatch],
  )

  const selectFlexibleDate = useCallback(
    (isFlexibleDate: boolean) => {
      if (!isFlexibleDate) {
        globalSearchDispatch({
          type: GlobalSearchStateActions.UNSET_CHECKIN_DATE,
        })
        globalSearchDispatch({
          type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE,
        })
      }
      globalSearchDispatch({
        type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED,
        selected: isFlexibleDate,
      })
    },
    [globalSearchDispatch],
  )

  const onSetOccupancies = useCallback(
    (occupancies: Array<App.Occupants>) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_OCCUPANCIES,
        occupancies,
      })
    },
    [globalSearchDispatch],
  )

  const onSearch = useCallback(() => {
    setImmersiveMode({ tripId, sectionId })
  }, [setImmersiveMode, tripId, sectionId])

  const source = useTripClientSource()
  const query = useMemo(
    () =>
      encodeSearchParams({
        urlSearch: '',
        searchItem: searchItem || ANYWHERE_SEARCH_ITEM,
        dates: {
          checkIn: checkinDate ?
              checkinDate.format(ISO_DATE_FORMAT) :
            undefined,
          checkOut: checkoutDate ?
              checkoutDate.format(ISO_DATE_FORMAT) :
            undefined,
        },
        rooms: occupancies,
        isFlexibleWithDate: isFlexibleDateSelected,
      }).toString() + `&source=${source}`,
    [
      searchItem,
      checkinDate,
      checkoutDate,
      occupancies,
      isFlexibleDateSelected,
      source,
    ],
  )

  const modalTitle = intl.formatMessage({
    id: 'oDq-5bbIvaJUmoBlQwXR',
    defaultMessage: 'Search accommodation offers',
    description: 'Modal title for accommodation search in trip planner',
  })

  const bodyDescription = intl.formatMessage({
    id: 'cOAHzbPg1QdSBaV27aDT',
    defaultMessage: 'Save up to 70% off on exclusive accommodation offers',
    description: 'Promotional description text for accommodation search in trip planner',
  })

  const checkInLabel = intl.formatMessage({
    id: 'dLFugWZZkWB40tuN0JW0',
    defaultMessage: 'Check-in',
    description: 'Label for check-in date in accommodation search date picker',
  })

  const checkOutLabel = intl.formatMessage({
    id: 'wx7mQsK-_lhP4kh62yKu',
    defaultMessage: 'Check out',
    description: 'Label for check-out date in accommodation search date picker',
  })

  const searchButtonLabel = intl.formatMessage({
    id: '5uNrcN4a82GrP56OtV9t',
    defaultMessage: 'Search',
    description: 'Button text to submit accommodation search in trip planner',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <BodyText variant="medium" colour="light-foreground-alt">
              {bodyDescription}
            </BodyText>
            <VerticalSpacer gap={12}>
              <PlaceInputTrigger
                subTitle={searchItem?.format.mainText}
                onClick={() =>
                  openModal(
                    SearchPlace,
                    {
                      searchView: 'ACCOMMODATION' as const,
                      tripId,
                    },
                    {
                      height: 'full',
                    },
                  )}
              />
              <DatesInputTrigger
                checkinDate={checkinDate}
                checkoutDate={checkoutDate}
                onClick={() =>
                  openModal(SearchDatePicker, {
                    initStartDate: checkinDate,
                    initEndDate: checkoutDate,
                    initVisibleDate:
                      !checkinDate && !checkoutDate ?
                        trip?.startDate :
                        undefined,
                    initIsFlexibleDateSelected: isFlexibleDateSelected,
                    minDate: MIN_SEARCH_DATE,
                    onSetDates,
                    onSetFlexibleDates: selectFlexibleDate,
                    startLabel: checkInLabel,
                    endLabel: checkOutLabel,
                  })}
              />
              <GuestsInputTrigger
                occupancies={occupancies}
                onClick={() =>
                  openModal(SearchOccupancyPicker, {
                    tripId,
                    onSetOccupancies,
                    initOccupants: occupancies,
                  })}
              />
            </VerticalSpacer>
            <Group direction="horizontal-reverse">
              <TextButton
                kind="primary"
                fit={isMobile ? 'full-width' : undefined}
                to={'/search?' + query}
                onClick={onSearch}
                size="large"
              >
                {searchButtonLabel}
              </TextButton>
            </Group>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchAccommodation
